import { useEffect, useState } from 'react';
import { App, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMsal } from '@azure/msal-react';
import { GetAccessToken } from '../../utils/auth-utils';
import { scopes } from '../../authConfig';
import { getPriorityBreakdown } from '../../services/admin-page-service';
import type { CallableCasesAnalyticsRow } from '../../dto/admin-config-models';
import { Header } from '../header/header';
import './priority-breakdown-page.css';

const { Title, Text } = Typography;

const renderCallableCell = (callable: number, assignedCount: number) =>
  assignedCount > 0 ? (
    <>
      {callable}{' '}
      <span className="priority-breakdown-assigned-count">
        (+{assignedCount} assigned)
      </span>
    </>
  ) : (
    callable
  );

const columns: ColumnsType<CallableCasesAnalyticsRow> = [
  {
    title: 'Priority',
    dataIndex: 'groupKey',
    key: 'groupKey',
    sorter: (a, b) => Number(a.groupKey) - Number(b.groupKey),
    defaultSortOrder: 'descend',
  },
  {
    title: 'Callable in Autodialer',
    dataIndex: 'callableInAutodialer',
    key: 'callableInAutodialer',
    align: 'right',
    onCell: (record) => ({
      className: record.assignedInAutodialer ? 'priority-breakdown-assigned-cell' : undefined,
    }),
    render: (value: number, record) => renderCallableCell(value, record.assignedCountInAutodialer),
  },
  {
    title: 'Callable in BookingOS',
    dataIndex: 'callableInBookingos',
    key: 'callableInBookingos',
    align: 'right',
    onCell: (record) => ({
      className: record.assignedInBookingos ? 'priority-breakdown-assigned-cell' : undefined,
    }),
    render: (value: number, record) => renderCallableCell(value, record.assignedCountInBookingos),
  },
];

const PriorityBreakdown = () => {
  const { notification } = App.useApp();
  const { instance, inProgress } = useMsal();

  const [rows, setRows] = useState<CallableCasesAnalyticsRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const data = await getPriorityBreakdown(token.accessToken);
      if (data) {
        setRows(data);
      } else {
        notification.error({ message: 'Failed to load priority breakdown' });
      }
    } catch (e) {
      notification.error({ message: 'Failed to load priority breakdown', description: String(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [instance, inProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ padding: 24, flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <Title level={4} style={{ margin: 0 }}>
          Priority Breakdown
        </Title>
      </div>
      <div className="priority-breakdown-legend">
        <span className="priority-breakdown-legend-swatch" aria-hidden="true" />
        <Text type="secondary">Callable counts show unique customers, not opportunities. Highlighted cell: agents are currently assigned at this priority in that context — "(+n assigned)" shows how many are being worked on top.</Text>
      </div>

      <Table
        className="priority-breakdown-table"
        rowKey="groupKey"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={false}
        size="small"
        sticky
      />
    </div>
  );
};

export const PriorityBreakdownPage = () => (
  <>
    <Header />
    <PriorityBreakdown />
  </>
);
